import React, { useEffect, useState } from "react";

const presets = { AED: 1, USD: 3.6725, EUR: 4.0, INR: 0.044 };
const fmtAED = (n) => (Number(n) || 0).toLocaleString("en-AE", { style: "currency", currency: "AED" });

export default function FreightCalculator({ onClose, defaultVat = 5 }) {
  const [currency, setCurrency] = useState("AED");
  const [rate, setRate] = useState(presets.AED);

  // inputs (entered in selected currency)
  const [goods, setGoods] = useState("");
  const [freight, setFreight] = useState("");
  const [other, setOther] = useState("");
  const [qty, setQty] = useState("");
  const [vat, setVat] = useState(String(defaultVat ?? 5));

  // customs: pct or fixed
  const [custMode, setCustMode] = useState("pct"); // 'pct' | 'fixed'
  const [custPct, setCustPct] = useState("5");
  const [custFixed, setCustFixed] = useState("");

  // selling: markup% or explicit price (last changed wins)
  const [markup, setMarkup] = useState("20");
  const [price, setPrice] = useState("");
  const [lastChanged, setLastChanged] = useState("markup");

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => setRate(presets[currency] ?? rate), [currency]); // keep last rate for OTHER

  const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const toAED = (v) => num(v) * num(rate);

  const goodsAED   = toAED(goods);
  const freightAED = toAED(freight);
  const otherAED   = toAED(other);

  const customsAED = custMode === "fixed" ? toAED(custFixed) : goodsAED * (num(custPct) / 100);
  const qtyN = Math.max(1, num(qty));
  const landedTotalAED = goodsAED + freightAED + customsAED + otherAED;
  const unitCostAED = landedTotalAED / qtyN;

  const sellingPerUnitAED =
    lastChanged === "price" && num(price) > 0
      ? toAED(price)
      : unitCostAED * (1 + num(markup) / 100);

  const effectiveMarkupPct = unitCostAED > 0 ? ((sellingPerUnitAED / unitCostAED - 1) * 100) : 0;

  const revenueExAED  = sellingPerUnitAED * qtyN;
  const revenueIncAED = revenueExAED * (1 + num(vat) / 100);
  const profitExAED   = revenueExAED - landedTotalAED;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center"
      onClick={(e)=> e.target === e.currentTarget && onClose?.()}
    >
      <div className="bg-white w-[min(920px,94vw)] max-h-[90vh] overflow-auto rounded-xl shadow-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold">Calculation (Freight &amp; Price)</h3>
          <button onClick={onClose} className="px-2 py-1 rounded hover:bg-gray-100">✕</button>
        </div>

        {/* Currency */}
        <div className="grid grid-cols-3 gap-3 mb-3">
          <div>
            <label className="block text-sm font-medium mb-1">Currency</label>
            <select
              className="border rounded px-2 py-1 w-full"
              value={currency}
              onChange={(e)=> setCurrency(e.target.value)}
            >
              <option value="AED">AED</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="INR">INR</option>
              <option value="OTHER">Other…</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium mb-1">FX rate to AED (1 {currency} = ? AED)</label>
            <input type="number" step="0.0001" className="border rounded px-2 py-1 w-full" value={rate} onChange={(e)=> setRate(e.target.value)} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Goods value ({currency})</label>
            <input className="border rounded px-2 py-1 w-full" value={goods} onChange={(e)=> setGoods(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Freight ({currency})</label>
            <input className="border rounded px-2 py-1 w-full" value={freight} onChange={(e)=> setFreight(e.target.value)} />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Customs</label>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1 text-sm">
                <input type="radio" checked={custMode==="pct"} onChange={()=> setCustMode("pct")} />
                % of Goods
              </label>
              <input className="border rounded px-2 py-1 w-20" type="number" value={custPct} onChange={(e)=> setCustPct(e.target.value)} disabled={custMode!=="pct"} />
              <label className="flex items-center gap-1 text-sm ml-4">
                <input type="radio" checked={custMode==="fixed"} onChange={()=> setCustMode("fixed")} />
                {currency} fixed
              </label>
              <input className="border rounded px-2 py-1 w-32" type="number" value={custFixed} onChange={(e)=> setCustFixed(e.target.value)} disabled={custMode!=="fixed"} />
            </div>
            <div className="text-xs text-gray-600 mt-1">Calculated customs: <b>{fmtAED(customsAED)}</b></div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Quantity (units)</label>
            <input className="border rounded px-2 py-1 w-full" value={qty} onChange={(e)=> setQty(e.target.value)} />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Other charges ({currency})</label>
            <input className="border rounded px-2 py-1 w-full" placeholder="D2D / handling / etc." value={other} onChange={(e)=> setOther(e.target.value)} />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">VAT %</label>
            <input className="border rounded px-2 py-1 w-full" value={vat} onChange={(e)=> setVat(e.target.value)} />
          </div>

          <div className="border rounded p-3">
            <div className="font-semibold mb-2">Landed Cost</div>
            <div className="text-sm">Total: <b>{fmtAED(landedTotalAED)}</b></div>
            <div className="text-sm">Per unit (cost): <b>{fmtAED(unitCostAED)}</b></div>
          </div>

          <div className="border rounded p-3">
            <div className="font-semibold mb-2">Selling Price</div>

            <div className="grid grid-cols-2 gap-2 items-end">
              <div>
                <label className="block text-xs font-medium mb-1">Markup % (over cost)</label>
                <input
                  className="border rounded px-2 py-1 w-full"
                  type="number"
                  value={lastChanged==="price" ? effectiveMarkupPct.toFixed(2) : markup}
                  onChange={(e)=>{ setMarkup(e.target.value); setLastChanged("markup"); }}
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Per unit price (ex-VAT) — {currency}</label>
                <input
                  className="border rounded px-2 py-1 w-full"
                  type="number"
                  value={price}
                  onChange={(e)=> { setPrice(e.target.value); setLastChanged("price"); }}
                  placeholder="Optional: overrides markup"
                />
              </div>
            </div>

            <div className="text-sm mt-2">
              Per unit (ex-VAT): <b>{fmtAED(sellingPerUnitAED)}</b><br/>
              Per unit (inc-VAT): <b>{fmtAED(sellingPerUnitAED * (1 + Number(vat||0)/100))}</b>
            </div>
          </div>

          <div className="md:col-span-2 border rounded p-3">
            <div className="font-semibold mb-2">Totals (for the quantity)</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
              <div>Revenue (ex-VAT): <b>{fmtAED(revenueExAED)}</b></div>
              <div>Revenue (inc-VAT): <b>{fmtAED(revenueIncAED)}</b></div>
              <div>Profit (ex-VAT): <b>{fmtAED(profitExAED)}</b></div>
            </div>
          </div>
        </div>

        <div className="mt-4 text-right">
          <button onClick={onClose} className="px-3 py-1 rounded bg-blue-600 text-white">Close</button>
        </div>
      </div>
    </div>
  );
}
